import * as React from "react";
import { useHistory } from "react-router";
import { useEffect, useRef } from "react";
// import fetchJsonp removed — we now use the shared fetchJson wrapper instead
import { _Styles } from "../../content/styles/Page.styles";
import { nextGenService } from "../../services/NextGenService";
import { IUserDetail } from "../../models/IUserDetail";
import { IUserRoleDetail } from "../../models/IUserRoleDetail";
import {
    encryptString,
    isNullOrEmpty,
    refreshSessionStorageKey,
    getEntityDataFromSession,
    getRuleValidationMessage,
} from "../../components/shared/common/util";
import { SessionStorageConsts } from "../../models/SessionStorageConsts";
import { UIControlsText } from "../../content/UIControlsText";
import { Guid } from "../../infrastructure/Guid";
import { ApplicationConstants } from "../../models/ApplicationConstants";
import { MessageTypeText } from "../../models/MessageTypeText";

interface AuthStatus {
    isAuthenticated: boolean;
    isAuthorized: boolean;
    email?: string;
    userName?: string;
    puid?: string;
    reason?: string;
    message?: string;
}

const sessionResourceData = getEntityDataFromSession<any>(
    SessionStorageConsts.resourceStrings,
);
const _loadingStyles = {
    container: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "40vh",
        padding: "24px",
    } as React.CSSProperties,
    card: {
        display: "flex",
        alignItems: "center",
        gap: "18px",
        padding: "20px 28px",
        borderRadius: 10,
        boxShadow: "0 6px 18px rgba(16,24,40,0.08)",
        background: "linear-gradient(180deg, #ffffff, #fbfbfd)",
        maxWidth: 720,
        width: "100%",
    } as React.CSSProperties,
    heading: {
        margin: 0,
        fontSize: "1.25rem",
        fontWeight: 600,
        color: "#0f172a",
    } as React.CSSProperties,
    sub: {
        margin: "6px 0 0 0",
        fontSize: "0.95rem",
        color: "#475569",
        lineHeight: 1.4,
    } as React.CSSProperties,
    spinner: {
        width: 56,
        height: 56,
        borderRadius: "50%",
        border: "6px solid rgba(99,102,241,0.12)",
        borderTopColor: "#6366f1", // indigo-500
        animation: "ngp-spin 1s linear infinite",
        flexShrink: 0,
    } as React.CSSProperties,
};
export const AAdAuth: React.FunctionComponent = () => {
    const history = useHistory();
    const initRef = useRef(false);
    let loggedInUser: IUserDetail;

    const enableAuth = process.env.REACT_APP_AUTHORIZATION_ENABLED === "true";
    const USER_DETAILS_URI = process.env.REACT_APP_RSLR_USERDETAILS_API_URI || "";
    const TOKEN_URI = process.env.REACT_APP_RSLR_ACCESS_TOKEN || "";
    const ACCOUNT_URI = process.env.REACT_APP_RSLR_ACCOUNT_API_URI || "";
    const AUTH_STATUS_URI = process.env.REACT_APP_CHECK_AUTH_STATUS_URI || "";
    const timeoutMs = Number(process.env.REACT_APP_PCAPI_TIMEOUT_MS) || 15000;

    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;
        bootstrap();
    }, []);

    async function bootstrap() {
        try {
            // 1) Get user details first
            const userJson = await fetchJson(USER_DETAILS_URI);
            loggedInUser = {
                email: userJson.Email,
                firstName: userJson.FirstName,
                lastName: userJson.LastName,
                name: userJson.Name,
                puid: userJson.PUID,
                userName: userJson.UserName,
                userRoleDetails: [],
            };

            // 2) Then check auth status before fetching access token
            let authStatus: AuthStatus | null = null;
            if (enableAuth) {
                authStatus = await checkAuthStatus();

                if (!authStatus.isAuthorized) {
                    // Do not proceed to fetch access token
                    history.push({
                        pathname: "/errorPage",
                        state: { message: UIControlsText.UnauthorizedUserError },
                    });
                    return;
                }
            }

            if (enableAuth) {
                await fetchToken();
            }

            if (
                loggedInUser.email
                    .toLowerCase()
                    .endsWith(ApplicationConstants.microsoftEmail)
            ) {
                FetchUserRoles();
            } else {
                if (enableAuth) {
                    FetchAccountsDetails();
                } else {
                    FetchAccountsDetailsFromRslr();
                }
            }
        } catch (ex: any) {
            history.push({
                pathname: "/errorPage",
                state: { message: ex.toString() },
            });
        }
    }

    // Checks auth status from backend (using same fetchJson pattern as fetchToken)
    async function checkAuthStatus(): Promise<AuthStatus> {
        const data = await fetchJson(AUTH_STATUS_URI);
        const status: AuthStatus = {
            isAuthenticated: !!data.isAuthenticated,
            isAuthorized: !!data.isAuthorized,
            email: data.email,
            userName: data.userName,
            puid: data.puid,
            reason: data.reason,
            message: data.message,
        };
        return status;
    }

    async function fetchToken() {
        const tokenJson = await fetchJson(TOKEN_URI);
        const accessToken: string = tokenJson?.AccessToken ?? tokenJson?.accessToken ?? "";
        const expiresOn: string = tokenJson?.ExpiresOn ?? tokenJson?.expiresOn ?? "";
        if (accessToken) {
            sessionStorage.setItem(SessionStorageConsts.nextGenAPIPCUserToken, accessToken);
            sessionStorage.setItem(SessionStorageConsts.userTokenExpiry, expiresOn);
        } else {
            throw new Error("Failed to retrieve access token");
        }
    }

    /**
     * Checks an axios error (or any caught error) for a "login_required" response
     * from the backend. If found, clears session and redirects to the login URL.
     * Returns true if it handled the redirect, false otherwise.
     */
    function handleLoginRequiredError(error: any): boolean {
        const data = error?.response?.data;
        if (data?.code === "login_required") {
            sessionStorage.clear();

            // Redirect to server-side SignOut which clears the HttpOnly
            // auth cookie, abandons the session, and triggers AAD sign-out.
            const logoutUrl = process.env.REACT_APP_RSLR_LOGOUT_API_URI;
            window.location.assign(logoutUrl || "/Home/SignOut");
            return true;
        }
        return false;
    }

    // Replaces JSONP for sensitive endpoints only
    async function fetchJson(url: string): Promise<any> {
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const res = await fetch(url, {
                credentials: "include",
                signal: controller.signal,
            });
            if (!res.ok) {
                // Handle 401 — session expired / login required
                if (res.status === 401) {
                    sessionStorage.clear();

                    // Redirect to server-side SignOut which clears the HttpOnly
                    // auth cookie, abandons the session, and triggers AAD sign-out.
                    // The server then redirects the user back to the login page.
                    const logoutUrl = process.env.REACT_APP_RSLR_LOGOUT_API_URI;
                    window.location.assign(logoutUrl || "/Home/SignOut");

                    // Return a pending Promise so the caller (bootstrap) permanently waits 
                    // and doesn't hit a TypeError before the browser redirects.
                    return new Promise(() => {});
                }
                throw new Error(`HTTP ${res.status}`);
            }
            return res.json();
        } finally {
            clearTimeout(t);
        }
    }

    // Gets the Org Account Details via NextGen API for External Accounts.
    function FetchAccountsDetails() {
        nextGenService
            .getUserAccountDetails(loggedInUser.email)
            .then(async (response) => {
                if (!isNullOrEmpty(response)) {
                    // Get the Org and Loc Details from the response.
                    if (response.data != null && response.data.length > 0) {
                        const locationList = response.data;
                        loggedInUser.organizationId = locationList[0]?.organizationId;
                        loggedInUser.organizationName = locationList[0]?.organizationName;
                        locationList.forEach((locationObj: IUserRoleDetail) => {
                            if (!isNullOrEmpty(locationObj)) {
                                loggedInUser.userRoleDetails?.push(locationObj);
                            }
                        });
                        FetchUserRoles();
                    } else {
                        history.push({
                            pathname: "/errorPage",
                            state: {
                                message: getRuleValidationMessage(
                                    sessionResourceData,
                                    MessageTypeText.OpportunityValidationError,
                                    "OrganizationInfoMissing",
                                    UIControlsText.OrganizationInfoMissing,
                                ),
                            },
                        });
                    }
                } else {
                    history.push({
                        pathname: "/errorPage",
                        state: {
                            message: getRuleValidationMessage(
                                sessionResourceData,
                                MessageTypeText.OpportunityValidationError,
                                "OrganizationInfoMissing",
                                UIControlsText.OrganizationInfoMissing,
                            ),
                        },
                    });
                }
            })
            .catch((ex: { toString: () => any }) => {
                console.log("parsing failed", ex);
                if (handleLoginRequiredError(ex)) return;
                history.push({
                    pathname: "/errorPage",
                    state: { message: ex.toString() },
                });
            });
    }

    // Legacy accounts via existing JSONP (converted to fetch to avoid JSONP/XSS)
    async function FetchAccountsDetailsFromRslr() {
        try {
            const result = await fetchJson(ACCOUNT_URI);

            // Accept both object responses and double-serialized JSON strings
            let parsed: any;
            if (typeof result === "string") {
                parsed = JSON.parse(result);
            } else {
                parsed = result;
            }

            const orgKeys = Object.keys(parsed || {});
            loggedInUser.organizationId = orgKeys.length
                ? orgKeys[0].split("-")[0].trim()
                : "";

            if (!isNullOrEmpty(loggedInUser.organizationId)) {
                Object.keys(parsed[orgKeys[0]]).forEach((key) => {
                    if (!isNullOrEmpty(key)) {
                        loggedInUser.userRoleDetails?.push({
                            locationId: key.split("-")[0].trim(),
                        } as IUserRoleDetail);
                    }
                });
                FetchUserRoles();
            } else {
                organizationError();
            }
        } catch (ex) {
            orgFetchError(ex);
        }
    }

    function FetchUserRoles() {
        sessionStorage.setItem(SessionStorageConsts.userEmail, loggedInUser.email);
        sessionStorage.removeItem(SessionStorageConsts.partnerCenterOnboardingPortalMessage);
        refreshSessionStorageKey(
            SessionStorageConsts.correlationId,
            Guid.newGuid(),
        );

        // Next Gen API Call to get the user roles
        nextGenService
            .getUserDetails(loggedInUser)
            .then(async (response) => {
                if (
                    !isNullOrEmpty(response.data.userRoleDetails) &&
                    !isNullOrEmpty(loggedInUser.userRoleDetails)
                ) {
                    loggedInUser.userRoleDetails = response.data.userRoleDetails;

                    const hasAnyMappedAccountWithPcFlag = loggedInUser.userRoleDetails?.some(
                        (role) => !isNullOrEmpty(role.pcFlag)
                    );
                    const hasAnyNonAdrOrDmpRole = loggedInUser.userRoleDetails?.some(
                        (role) => role.userSubRole !== "ADR" && role.userSubRole !== "DMP"
                    );
                    const hasAnyUserRoles = (loggedInUser.userRoleDetails?.length ?? 0) > 0;

                    // Redirect to error page when enforcePCFlagCheck is enabled,
                    // no mapped account has a pcFlag, and all roles are ADR/DMP.
                    const enforcePCFlagCheck = response.data.enforcePCFlagCheck === true;
                    if (enforcePCFlagCheck && !hasAnyMappedAccountWithPcFlag && !hasAnyNonAdrOrDmpRole) {
                        history.push({
                            pathname: "/errorPage",
                            state: {
                                message: UIControlsText.PartnerCenterOnboardingError,
                                suppressTechnicalDetails: true,
                            },
                        });
                        return;
                    }

                    // Banner: show onboarding message when pcFlag is null for all accounts and all roles are ADR/DMP
                    if (hasAnyUserRoles && !hasAnyMappedAccountWithPcFlag && !hasAnyNonAdrOrDmpRole) {
                        const partnerCenterLinkText = getRuleValidationMessage(
                            sessionResourceData,
                            MessageTypeText.FieldLabelText,
                            "PartnerCenterText",
                            UIControlsText.PartnerCenterText,
                        );
                        const partnerCenterPortalLinkText = getRuleValidationMessage(
                            sessionResourceData,
                            MessageTypeText.FieldLabelText,
                            "PartnerCenterPortalText",
                            UIControlsText.PartnerCenterPortalText,
                        );
                        const partnerCenterLink = `<a href="${process.env.REACT_APP_PARTNER_CENTER_URL}" target="_blank" rel="noopener noreferrer">${partnerCenterLinkText}</a>`;
                        const partnerCenterPortalLink = `<a href="${process.env.REACT_APP_ADDITIONAL_ONBOARDING_INFO_URL}" target="_blank" rel="noopener noreferrer">${partnerCenterPortalLinkText}</a>`;
                        const onboardingPortalMessage = getRuleValidationMessage(
                            sessionResourceData,
                            MessageTypeText.OpportunityValidationError,
                            "PartnerCenterOnboardingPortalError",
                            UIControlsText.PartnerCenterOnboardingPortalError,
                        )
                            .replace("{PartnerCenterLink}", partnerCenterLink)
                            .replace("{PartnerCenterPortalLink}", partnerCenterPortalLink);

                        sessionStorage.setItem(
                            SessionStorageConsts.partnerCenterOnboardingPortalMessage,
                            onboardingPortalMessage,
                        );
                    }
                    sessionStorage.setItem(
                        SessionStorageConsts.loggedInUserInfo,
                        encryptString(JSON.stringify(loggedInUser)),
                    );

                    // Store self-serve team membership flag so the left menu can gate visibility.
                    if (response.data.isPartOfSelfServeTeam === true) {
                        sessionStorage.setItem(SessionStorageConsts.isSelfServeEnabled, UIControlsText.YesText);
                    } else {
                        sessionStorage.removeItem(SessionStorageConsts.isSelfServeEnabled);
                    }

                    const userRequestedUrL = sessionStorage.getItem(
                        SessionStorageConsts.userRequestedUrL,
                    );
                    if (!isNullOrEmpty(userRequestedUrL)) {
                        sessionStorage.removeItem(SessionStorageConsts.userRequestedUrL);
                        history.push(userRequestedUrL || "/homePage");
                        window.location.reload();
                    } else {
                        history.push("/homePage");
                        window.location.reload();
                    }
                } else {
                    history.push("/homePage");
                    window.location.reload();
                }
            })
            .catch((error) => {
                console.log("parsing failed", error);

                // If backend says login_required, log out and redirect to sign-in
                if (handleLoginRequiredError(error)) return;

                const response = error?.response;
                let backendMessage: string | undefined;

                if (response) {
                    if (typeof response.data === "string") {
                        // Handles cases like: StatusCode(403, "Organization ID is not authorized for this user")
                        backendMessage = response.data;
                    } else {
                        backendMessage =
                            response.data?.message ||
                            response.data?.errorMessage ||
                            response.data?.error?.message;
                    }
                }

                if (backendMessage && backendMessage.trim()) {
                    history.push({
                        pathname: "/errorPage",
                        state: { message: backendMessage },
                    });
                } else {
                    history.push({
                        pathname: "/errorPage",
                        state: {
                            message: getRuleValidationMessage(
                                sessionResourceData,
                                MessageTypeText.OpportunityValidationError,
                                "NotFoundApiError",
                                UIControlsText.NotFoundApiError,
                            ),
                        },
                    });
                }
            });
    }

    function organizationError() {
        history.push({
            pathname: "/errorPage",
            state: {
                message: getRuleValidationMessage(
                    sessionResourceData,
                    MessageTypeText.OpportunityValidationError,
                    "OrganizationInfoMissing",
                    UIControlsText.OrganizationInfoMissing,
                ),
            },
        });
    }

    function orgFetchError(ex: any) {
        history.push({
            pathname: "/errorPage",
            state: { message: ex.toString() },
        });
    }

    // Replace the current return(...) with this block:
    return (
        <div>
            {/* small keyframes injected locally for the spinner */}
            <style>{`
            @keyframes ngp-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
        `}</style>

            <div className={_Styles.contentStyle}>
                <div style={_loadingStyles.container}>
                    <div style={_loadingStyles.card} aria-live="polite" role="status">
                        <div style={_loadingStyles.spinner} aria-hidden="true" />
                        <div>
                            <h3 style={_loadingStyles.heading}>
                                Loading your account and permissions…
                            </h3>
                            <p style={_loadingStyles.sub}>
                                We're securely retrieving your profile, accounts and roles. This
                                usually takes a few seconds — we'll take you straight to your
                                dashboard once it's ready.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
